import { useMemo, type ReactElement } from 'react';
import type { CaptureEvent } from '@opsrev/shared';
import { formatClock } from '../format';

/** Events we surface as clickable markers on the timeline. */
type MarkerKind = 'marker' | 'mouse_click' | 'key' | 'narration_segment';

interface TimelineMarker {
  kind: MarkerKind;
  tRelMs: number;
  label: string;
}

function toMarker(ev: CaptureEvent): TimelineMarker | null {
  switch (ev.type) {
    case 'marker':
      return { kind: 'marker', tRelMs: ev.tRelMs, label: ev.payload.text || 'Marker' };
    case 'mouse_click':
      return {
        kind: 'mouse_click',
        tRelMs: ev.tRelMs,
        label: `${ev.payload.double ? 'Double-click' : 'Click'} (${ev.payload.button})`,
      };
    case 'key':
      return {
        kind: 'key',
        tRelMs: ev.tRelMs,
        label: ev.payload.redacted ? 'Key (redacted)' : `Key ${ev.payload.char ?? ev.payload.code}`,
      };
    case 'narration_segment':
      return {
        kind: 'narration_segment',
        tRelMs: ev.tRelMs,
        label: ev.payload.text ? `“${ev.payload.text}”` : 'Narration',
      };
    default:
      return null;
  }
}

const KIND_LABEL: Record<MarkerKind, string> = {
  marker: 'Step markers',
  mouse_click: 'Clicks',
  key: 'Keystrokes',
  narration_segment: 'Narration',
};

/**
 * Renders capture events along a horizontal timeline scaled to the recording
 * duration. The playhead reflects the video's currentTime; clicking a marker
 * seeks the video (via onSeek, in seconds).
 */
export function EventTimeline({
  events,
  durationMs,
  currentTimeSec,
  onSeek,
}: {
  events: CaptureEvent[];
  durationMs: number;
  currentTimeSec: number;
  onSeek: (sec: number) => void;
}): ReactElement {
  const markers = useMemo(
    () =>
      events
        .map(toMarker)
        .filter((m): m is TimelineMarker => m !== null)
        .sort((a, b) => a.tRelMs - b.tRelMs),
    [events],
  );

  // Group by kind into lanes so dense events (clicks/keys) don't overlap markers.
  const lanes: MarkerKind[] = ['marker', 'mouse_click', 'key', 'narration_segment'];
  const present = lanes.filter((k) => markers.some((m) => m.kind === k));

  const total = durationMs > 0 ? durationMs : Math.max(1, ...markers.map((m) => m.tRelMs));
  const pct = (t: number) => `${Math.min(100, Math.max(0, (t / total) * 100))}%`;
  const playheadPct = pct(currentTimeSec * 1000);

  if (markers.length === 0) {
    return <p className="muted">No timeline events were captured for this session.</p>;
  }

  return (
    <div className="timeline">
      <div className="timeline-axis">
        <span>0:00</span>
        <span>{formatClock(total / 1000)}</span>
      </div>
      <div className="timeline-lanes">
        {present.map((kind) => (
          <div className="timeline-lane" key={kind}>
            <span className="lane-label">{KIND_LABEL[kind]}</span>
            <div className="lane-track">
              {markers
                .filter((m) => m.kind === kind)
                .map((m, i) => (
                  <button
                    key={`${kind}-${i}-${m.tRelMs}`}
                    className={`tl-marker tl-${kind}`}
                    style={{ left: pct(m.tRelMs) }}
                    title={`${formatClock(m.tRelMs / 1000)} — ${m.label}`}
                    onClick={() => onSeek(m.tRelMs / 1000)}
                    aria-label={`Seek to ${formatClock(m.tRelMs / 1000)}: ${m.label}`}
                  />
                ))}
            </div>
          </div>
        ))}
        <div className="timeline-playhead" style={{ left: playheadPct }} />
      </div>
    </div>
  );
}
